// Mobile translations file with lazy loading support
// This file imports translations from separate language files for better performance

import { en } from './translations/en';
import { tr } from './translations/tr';
import { de } from './translations/de';

// Static translations object for immediate access
export const translations = {
  en,
  tr,
  de
};

// Language type
export type Language = 'en' | 'tr' | 'de';

// Translation key type - inferred from English translations
export type TranslationKey = keyof typeof en;

// Translation type
export type Translation = typeof en;

// Lazy loading function for dynamic imports
export const loadTranslations = async (language: Language): Promise<Translation> => {
  switch (language) {
    case 'en':
      return en;
    case 'tr':
      return tr;
    case 'de':
      return de;
    default:
      return en; // fallback to English
  }
};

// Get translation function with type safety
export const getTranslation = (language: Language, key: TranslationKey): string => {
  const langTranslations = translations[language] || translations.en;
  return langTranslations[key] || translations.en[key] || key;
};

// Translation loader for React components
export const useTranslations = (language: Language) => {
  const currentTranslations = translations[language] || translations.en;

  const t = (key: TranslationKey): string => {
    return currentTranslations[key] || translations.en[key] || key;
  };

  return { t, translations: currentTranslations };
};

// Default export for backward compatibility
export default translations;
